import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  EuiBadge,
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiCallOut,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiEmptyPrompt,
  EuiFieldSearch,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiInMemoryTable,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import { AdminUser, useApi } from "../";
import { UserPermissionsFlyout } from "./userPermissions";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";

/** A user row with derived fields used by the table filters/sorting. */
type UserRow = AdminUser & {
  status: "active" | "inactive" | "invited";
  /** Joined organization names - a sortable/searchable string for the Org column. */
  orgLabel: string;
};

const isInvite = (id: string): boolean => id.startsWith("invite:");

/**
 * Lists ALL users (client-side searchable/paginated) with their organizations,
 * client + realm roles, last login to the app, and a link to the Keycloak admin
 * console. Client roles are assigned/revoked here; all mutations go through the
 * superuser-gated /api/v2/admin routes.
 */
export const AdminUsersRoles = (): JSX.Element => {
  const { Get, Post, Delete } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  // User whose effective permissions are shown in the flyout (null = closed).
  const [permsUser, setPermsUser] = useState<AdminUser | null>(null);

  // Filters. Default view: active users that have client access.
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive" | "invited"
  >("active");
  // Access = client role OR questionnaire membership. Default: any access.
  const [accessFilter, setAccessFilter] = useState<
    "all" | "any" | "role" | "questionnaire" | "none"
  >("any");
  const [pendingOnly, setPendingOnly] = useState<boolean>(false);
  const [text, setText] = useState<string>("");

  const loadRoles = async () => {
    try {
      const resp = await Get<any>({
        path: "v2/admin/roles",
        responseType: "json",
        token: keycloak?.token,
      });
      setAvailableRoles((resp?.data as any)?.data ?? []);
    } catch {
      /* non-fatal */
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const resp = await Get<any>({
        path: "v2/admin/users",
        responseType: "json",
        token: keycloak?.token,
      });
      // The API client never throws - it returns a response carrying success /
      // status / error. Inspect those so 403 renders a permission-denied state
      // and other failures show an inline error + retry.
      if (!resp.success) {
        if (resp.status === 403 || resp.status === 401) {
          setForbidden(true);
        } else {
          setError(
            resp.error?.message ||
              translate("anketa.admin.users.load.error", {
                defaultMessage: "Users could not be loaded.",
              }),
          );
        }
        setUsers([]);
        return;
      }
      setUsers((resp?.data as any)?.data ?? []);
    } catch {
      setError(
        translate("anketa.admin.users.load.error", {
          defaultMessage: "Users could not be loaded.",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak?.token) {
      loadRoles();
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const assign = async (user: AdminUser, role: string) => {
    if (!role || user.clientRoles.includes(role)) {
      return;
    }
    setBusyUser(user.id);
    // Optimistic: add the role locally, snapshot for rollback.
    const prevUsers = users;
    setUsers((us) =>
      us.map((u) =>
        u.id === user.id
          ? { ...u, clientRoles: [...u.clientRoles, role] }
          : u
      )
    );
    try {
      const resp = await Post<any>({
        path: `v2/admin/users/${user.id}/roles`,
        responseType: "json",
        token: keycloak?.token,
        value: { role },
      });
      if (!resp.success) {
        throw new Error(resp.error?.message);
      }
      addToast({
        color: "success",
        iconType: "checkInCircleFilled",
        title: translate("anketa.admin.users.role.assign.success", {
          values: { role },
          defaultMessage: 'Role "{{role}}" assigned.',
        }),
      });
    } catch (err) {
      setUsers(prevUsers);
      addToast({
        color: "danger",
        iconType: "alert",
        title: translate("anketa.admin.users.role.assign.error", {
          values: { role },
          defaultMessage: 'Role "{{role}}" could not be assigned.',
        }),
        text: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusyUser(null);
    }
  };

  const remove = async (user: AdminUser, role: string) => {
    setBusyUser(user.id);
    // Optimistic: drop the role locally, snapshot for rollback.
    const prevUsers = users;
    setUsers((us) =>
      us.map((u) =>
        u.id === user.id
          ? { ...u, clientRoles: u.clientRoles.filter((r) => r !== role) }
          : u
      )
    );
    try {
      const resp = await Delete<any>({
        path: `v2/admin/users/${user.id}/roles/${encodeURIComponent(role)}`,
        responseType: "json",
        token: keycloak?.token,
      });
      if (!resp.success) {
        throw new Error(resp.error?.message);
      }
      addToast({
        color: "success",
        iconType: "checkInCircleFilled",
        title: translate("anketa.admin.users.role.remove.success", {
          values: { role },
          defaultMessage: 'Role "{{role}}" removed.',
        }),
      });
    } catch (err) {
      setUsers(prevUsers);
      addToast({
        color: "danger",
        iconType: "alert",
        title: translate("anketa.admin.users.role.remove.error", {
          values: { role },
          defaultMessage: 'Role "{{role}}" could not be removed.',
        }),
        text: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusyUser(null);
    }
  };

  const columns: EuiBasicTableColumn<UserRow>[] = [
    {
      name: translate("anketa.admin.users.column.user", {
        defaultMessage: "User",
      }),
      field: "email",
      sortable: true,
      render: (_email: string, u: UserRow) => (
        <EuiText size="s">
          <strong>{u.email ?? u.username ?? u.id}</strong>
          {(u.firstName || u.lastName) && (
            <>
              <br />
              <span>{[u.firstName, u.lastName].filter(Boolean).join(" ")}</span>
            </>
          )}
        </EuiText>
      ),
    },
    {
      name: translate("anketa.admin.users.column.status", {
        defaultMessage: "Status",
      }),
      field: "status",
      sortable: true,
      width: "120px",
      render: (status: UserRow["status"]) =>
        status === "active" ? (
          <EuiHealth color="success">
            {translate("anketa.admin.users.status.active", {
              defaultMessage: "Active",
            })}
          </EuiHealth>
        ) : status === "inactive" ? (
          <EuiHealth color="danger">
            {translate("anketa.admin.users.status.inactive", {
              defaultMessage: "Inactive",
            })}
          </EuiHealth>
        ) : (
          <EuiHealth color="warning">
            {translate("anketa.admin.users.status.invited", {
              defaultMessage: "Invited",
            })}
          </EuiHealth>
        ),
    },
    {
      name: translate("anketa.admin.users.column.organization", {
        defaultMessage: "Organization",
      }),
      field: "orgLabel",
      sortable: true,
      render: (_orgLabel: string, u: UserRow) => (
        <EuiText size="s">
          {u.organizations && u.organizations.length > 0
            ? u.organizations.join(", ")
            : "-"}
          {u.invitationPending && u.pendingOrganizations && (
            <div>
              <EuiBadge color="warning">
                {translate("anketa.admin.users.invitation.label", {
                  values: { organizations: u.pendingOrganizations.join(", ") },
                  defaultMessage: "Invitation: {{organizations}}",
                })}
              </EuiBadge>
            </div>
          )}
        </EuiText>
      ),
    },
    {
      name: translate("anketa.admin.users.column.roles", {
        defaultMessage: "Roles",
      }),
      render: (u: UserRow) => (
        <>
          <EuiFlexGroup wrap responsive={false} gutterSize="xs" alignItems="center">
            {u.clientRoles.length === 0 && (
              <EuiFlexItem grow={false}>
                <EuiText size="s" color="subdued">
                  -
                </EuiText>
              </EuiFlexItem>
            )}
            {u.clientRoles.map((role) => (
              <EuiFlexItem grow={false} key={role}>
                <EuiBadge
                  color="hollow"
                  iconType="cross"
                  iconSide="right"
                  iconOnClick={() => remove(u, role)}
                  iconOnClickAriaLabel={translate(
                    "anketa.admin.users.role.remove.aria",
                    {
                      values: { role },
                      defaultMessage: "Remove role {{role}}",
                    },
                  )}
                >
                  {role}
                </EuiBadge>
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
          {u.realmRoles && u.realmRoles.length > 0 && (
            <EuiText size="xs" color="subdued">
              {translate("anketa.admin.users.realm.label", {
                values: { roles: u.realmRoles.join(", ") },
                defaultMessage: "Realm: {{roles}}",
              })}
            </EuiText>
          )}
        </>
      ),
    },
    {
      name: translate("anketa.admin.users.column.questionnaires", {
        defaultMessage: "Questionnaires",
      }),
      width: "150px",
      render: (u: UserRow) => {
        const list = u.questionnaires ?? [];
        if (list.length === 0) {
          return (
            <EuiText size="s" color="subdued">
              -
            </EuiText>
          );
        }
        return (
          <EuiToolTip content={list.join(", ")}>
            <EuiBadge color="accent">
              {list.length === 1
                ? translate("anketa.admin.users.questionnaires.count.one", {
                    values: { count: list.length },
                    defaultMessage: "{{count}} questionnaire",
                  })
                : translate("anketa.admin.users.questionnaires.count.other", {
                    values: { count: list.length },
                    defaultMessage: "{{count}} questionnaires",
                  })}
            </EuiBadge>
          </EuiToolTip>
        );
      },
    },
    {
      name: translate("anketa.admin.users.column.add.role", {
        defaultMessage: "Add role",
      }),
      width: "200px",
      render: (u: UserRow) => {
        if (isInvite(u.id)) {
          return (
            <EuiText size="xs" color="subdued">
              {translate("anketa.admin.users.no.account", {
                defaultMessage: "no account yet",
              })}
            </EuiText>
          );
        }
        const options: EuiComboBoxOptionOption[] = availableRoles
          .filter((r) => !u.clientRoles.includes(r))
          .map((r) => ({ label: r }));
        return (
          <EuiComboBox
            placeholder={translate("anketa.admin.users.role.select.placeholder", {
              defaultMessage: "Select role",
            })}
            singleSelection={{ asPlainText: true }}
            options={options}
            selectedOptions={[]}
            isLoading={busyUser === u.id}
            isDisabled={busyUser === u.id}
            onChange={(selected) => {
              const role = selected[0]?.label;
              if (role) {
                assign(u, role);
              }
            }}
            compressed
          />
        );
      },
    },
    {
      name: translate("anketa.admin.users.column.last.login", {
        defaultMessage: "Last login",
      }),
      field: "lastLogin",
      sortable: true,
      width: "150px",
      render: (lastLogin: number | null) =>
        lastLogin ? (
          <EuiToolTip content={moment(lastLogin).format("YYYY-MM-DD HH:mm")}>
            <EuiText size="s">{moment(lastLogin).fromNow()}</EuiText>
          </EuiToolTip>
        ) : (
          <EuiText size="s" color="subdued">
            -
          </EuiText>
        ),
    },
    {
      name: translate("anketa.admin.users.column.permissions", {
        defaultMessage: "Access",
      }),
      width: "80px",
      align: "center",
      render: (u: UserRow) =>
        isInvite(u.id) ? (
          <></>
        ) : (
          <EuiToolTip
            content={translate("anketa.admin.users.perms.view.tooltip", {
              defaultMessage: "View this user's effective permissions",
            })}
          >
            <EuiButtonIcon
              iconType="eye"
              aria-label={translate("anketa.admin.users.perms.view.aria", {
                defaultMessage: "View permissions",
              })}
              onClick={() => setPermsUser(u)}
            />
          </EuiToolTip>
        ),
    },
    {
      name: "Keycloak",
      width: "90px",
      align: "center",
      render: (u: UserRow) =>
        u.keycloakLink ? (
          <EuiToolTip
            content={translate("anketa.admin.users.keycloak.tooltip", {
              defaultMessage: "Open in Keycloak admin",
            })}
          >
            <EuiButtonIcon
              iconType="popout"
              aria-label={translate("anketa.admin.users.keycloak.aria", {
                defaultMessage: "Open Keycloak account",
              })}
              href={u.keycloakLink}
              target="_blank"
            />
          </EuiToolTip>
        ) : (
          <></>
        ),
    },
  ];

  // Derive the status field per user.
  const rows = useMemo<UserRow[]>(
    () =>
      users.map((u) => ({
        ...u,
        status: isInvite(u.id)
          ? "invited"
          : u.enabled === false
          ? "inactive"
          : "active",
        orgLabel: (u.organizations ?? []).join(", "),
      })),
    [users],
  );

  // Explicit, predictable filtering (free text + status + access + pending).
  // "access" = client role OR questionnaire membership.
  const filtered = useMemo<UserRow[]>(() => {
    const q = text.trim().toLowerCase();
    return rows.filter((r) => {
      const hasRole = r.clientRoles.length > 0;
      const hasQuestionnaire = (r.questionnaires?.length ?? 0) > 0;

      if (statusFilter !== "all" && r.status !== statusFilter) {
        return false;
      }
      if (accessFilter === "any" && !hasRole && !hasQuestionnaire) {
        return false;
      }
      if (accessFilter === "role" && !hasRole) {
        return false;
      }
      if (accessFilter === "questionnaire" && !hasQuestionnaire) {
        return false;
      }
      if (accessFilter === "none" && (hasRole || hasQuestionnaire)) {
        return false;
      }
      if (pendingOnly && !r.invitationPending) {
        return false;
      }
      if (q) {
        const haystack = [
          r.email,
          r.username,
          r.firstName,
          r.lastName,
          ...(r.organizations ?? []),
          ...(r.clientRoles ?? []),
          ...(r.realmRoles ?? []),
          ...(r.questionnaires ?? []),
          ...(r.pendingOrganizations ?? []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, text, statusFilter, accessFilter, pendingOnly]);

  if (forbidden) {
    return (
      <EuiEmptyPrompt
        iconType="lock"
        color="warning"
        title={
          <h2>
            {translate("anketa.admin.forbidden.title", {
              defaultMessage: "Access denied",
            })}
          </h2>
        }
        body={
          <p>
            {translate("anketa.admin.forbidden.body", {
              defaultMessage:
                "You do not have permission to view this section.",
            })}
          </p>
        }
      />
    );
  }

  return (
    <>
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.users.intro", {
          defaultMessage:
            "All users with organization, client roles, questionnaire access (by membership, independent of roles), last login and a link to the Keycloak account. Default view: active users with access (role or questionnaire). Client roles can be assigned/removed here.",
        })}
      </EuiText>
      <EuiSpacer size="m" />
      <EuiFlexGroup gutterSize="s" wrap alignItems="flexEnd">
        <EuiFlexItem style={{ minWidth: 240 }}>
          <EuiFieldSearch
            placeholder={translate("anketa.admin.users.search.placeholder", {
              defaultMessage: "Search by name, email, organization, role…",
            })}
            value={text}
            isClearable
            onChange={(e) => setText(e.target.value)}
            fullWidth
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSelect
            prepend={translate("anketa.admin.users.column.status", {
              defaultMessage: "Status",
            })}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            options={[
              {
                value: "all",
                text: translate("anketa.admin.users.filter.all", {
                  defaultMessage: "All",
                }),
              },
              {
                value: "active",
                text: translate("anketa.admin.users.status.active", {
                  defaultMessage: "Active",
                }),
              },
              {
                value: "inactive",
                text: translate("anketa.admin.users.status.inactive", {
                  defaultMessage: "Inactive",
                }),
              },
              {
                value: "invited",
                text: translate("anketa.admin.users.status.invited", {
                  defaultMessage: "Invited",
                }),
              },
            ]}
            compressed
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSelect
            prepend={translate("anketa.admin.users.filter.access", {
              defaultMessage: "Access",
            })}
            value={accessFilter}
            onChange={(e) => setAccessFilter(e.target.value as any)}
            options={[
              {
                value: "all",
                text: translate("anketa.admin.users.filter.all", {
                  defaultMessage: "All",
                }),
              },
              {
                value: "any",
                text: translate("anketa.admin.users.filter.access.any", {
                  defaultMessage: "with access (role or questionnaire)",
                }),
              },
              {
                value: "role",
                text: translate("anketa.admin.users.filter.access.role", {
                  defaultMessage: "client role only",
                }),
              },
              {
                value: "questionnaire",
                text: translate(
                  "anketa.admin.users.filter.access.questionnaire",
                  {
                    defaultMessage: "questionnaire only",
                  },
                ),
              },
              {
                value: "none",
                text: translate("anketa.admin.users.filter.access.none", {
                  defaultMessage: "without access",
                }),
              },
            ]}
            compressed
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSwitch
            label={translate("anketa.admin.users.filter.pending.only", {
              defaultMessage: "Pending invitations only",
            })}
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            compressed
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      {error && (
        <>
          <EuiCallOut title={error} color="danger" iconType="alert">
            <EuiButton
              color="danger"
              size="s"
              iconType="refresh"
              onClick={() => loadUsers()}
            >
              {translate("anketa.common.retry", { defaultMessage: "Retry" })}
            </EuiButton>
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}
      <EuiInMemoryTable<UserRow>
        items={filtered}
        columns={columns}
        loading={loading}
        itemId="id"
        message={
          loading
            ? translate("anketa.admin.users.loading", {
                defaultMessage: "Loading…",
              })
            : translate("anketa.admin.users.empty", {
                defaultMessage: "No users found",
              })
        }
        pagination={{ initialPageSize: 25, pageSizeOptions: [25, 50, 100] }}
        sorting={{ sort: { field: "email", direction: "asc" } }}
      />
      <EuiSpacer size="s" />
      <EuiText size="xs" color="subdued">
        {translate("anketa.admin.users.count.summary", {
          values: { filtered: filtered.length, total: rows.length },
          defaultMessage: "{{filtered}} of {{total}} users",
        })}
      </EuiText>
      <EuiButtonEmpty onClick={loadUsers} isDisabled={loading} iconType="refresh">
        {translate("anketa.admin.users.refresh", {
          defaultMessage: "Refresh",
        })}
      </EuiButtonEmpty>
      {permsUser && (
        <UserPermissionsFlyout
          user={permsUser}
          onClose={() => setPermsUser(null)}
        />
      )}
    </>
  );
};
